import { z } from 'zod';

/**
 * How money moves.
 *
 * Two lists, deliberately, because they answer different questions.
 *
 * `PaymentMethod` is what the DATABASE has ever accepted. Historical rows
 * carry these values and a report from last year must still be able to say
 * "bank transfer", so nothing is ever removed from it.
 *
 * `OFFERED_PAYMENT_METHODS` is what the clinic takes money by TODAY. It is a
 * business decision, not a schema one, and it changes without a migration.
 * A method with `available: false` is shown and cannot be picked — that is a
 * promise that it is coming, which is more honest than an absence the user
 * has to interpret.
 */
export const PaymentMethod = z.enum(['cash', 'card', 'transfer', 'cheque', 'other']);
export type PaymentMethod = z.infer<typeof PaymentMethod>;

export const OFFERED_PAYMENT_METHODS = [
  { value: 'cash', available: true },
  // Card acceptance is agreed but not yet live; shown so nobody wonders
  // whether it was forgotten.
  { value: 'card', available: false },
] as const satisfies readonly { value: PaymentMethod; available: boolean }[];

/** The methods a NEW payment may actually use. */
export const ACCEPTED_PAYMENT_METHODS = OFFERED_PAYMENT_METHODS.filter((m) => m.available).map(
  (m) => m.value,
) as PaymentMethod[];

/**
 * Validator for new payments. Deliberately NOT the full enum: refusing a
 * method the clinic does not accept is the point, and the message says which
 * ones it does.
 */
export const AcceptedPaymentMethod = PaymentMethod.refine(
  (m) => (ACCEPTED_PAYMENT_METHODS as string[]).includes(m),
  { message: `Only ${ACCEPTED_PAYMENT_METHODS.join(', ')} can be taken at the moment.` },
);
